// frontend/src/pages/CategoryPage.jsx
import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { PageSEO } from '../components/SEO/PageSEO';
import { Loader2, ChevronLeft, Grid3x3, List, Filter, X, Sun, Battery, Zap, Wrench, Home, Package, ChevronRight, Grid, LayoutGrid, Rows, SlidersHorizontal } from 'lucide-react';
import { productService, categoryService } from '../services/api';
import { ProductCard } from '../components/ProductCard';

const getCategoryIcon = (categoryName) => {
  const icons = {
    'پنل خورشیدی': Sun,
    'اینورتر': Zap,
    'باتری': Battery,
    'تجهیزات جانبی': Wrench,
    'استراکچر': Home,
    'سانورتر': Zap,
  };
  const Icon = icons[categoryName];
  return Icon ? <Icon className="w-10 h-10 md:w-12 md:h-12 text-blue-500" /> : null;
};

// گزینه‌های تعداد محصول در هر صفحه
const PAGE_SIZE_OPTIONS = [12, 24, 36, 48];

export const CategoryPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [pageSize, setPageSize] = useState(12);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [filters, setFilters] = useState({
    minPrice: '',
    maxPrice: '',
    sort: '-created_at',
    inStock: false,
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [slug, filters, pageSize]);

  useEffect(() => {
    fetchCategoryData();
  }, [slug, filters, currentPage, pageSize]);

  const fetchCategoryData = async () => {
    setLoading(true);
    try {
      const [categoryRes, subcategoriesRes] = await Promise.all([
        categoryService.getBySlug(slug),
        categoryService.getSubCategories(slug),
      ]);
      
      setCategory(categoryRes.data);
      setSubcategories(subcategoriesRes.data || []);
      
      const params = { 
        category: slug,
        ordering: filters.sort,
        page: currentPage,
        page_size: pageSize,
      };
      if (filters.minPrice) params.min_price = filters.minPrice;
      if (filters.maxPrice) params.max_price = filters.maxPrice;
      if (filters.inStock) params.in_stock = true;
      
      const productsRes = await productService.getAll(params);
      setProducts(productsRes.data.results || productsRes.data || []);
      setTotalCount(productsRes.data.count || 0);
      setTotalPages(Math.ceil((productsRes.data.count || 0) / pageSize));
    } catch (error) {
      console.error('Error fetching category:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setShowMobileFilters(false);
  };

  const clearFilters = () => {
    setFilters({
      minPrice: '',
      maxPrice: '',
      sort: '-created_at',
      inStock: false,
    });
    setShowMobileFilters(false);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const hasActiveFilters = filters.minPrice || filters.maxPrice || filters.inStock;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">دسته‌بندی یافت نشد</h2>
          <p className="text-gray-600 mb-4">دسته‌بندی مورد نظر شما وجود ندارد</p>
          <button onClick={() => navigate('/products')} className="text-blue-600 hover:underline">
            بازگشت به محصولات
          </button>
        </div>
      </div>
    );
  }

  const seoTitle = `خرید ${category.name} | سان ایران`;
  const seoDescription = `بهترین قیمت ${category.name} خورشیدی | فروش آنلاین انواع ${category.name} با گارانتی اصالت و کیفیت تضمینی`;

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  return (
    <>
      <PageSEO
        title={seoTitle}
        description={seoDescription}
        keywords={`${category.name}, خرید ${category.name}, قیمت ${category.name}, فروش ${category.name}, ${category.name} خورشیدی`}
        type="website"
      />

      <div className="min-h-screen bg-gray-50 py-4 md:py-8">
        <div className="container mx-auto px-3 md:px-4">
          {/* مسیر راهنما */}
          <div className="flex items-center gap-1 md:gap-2 text-xs md:text-sm text-gray-500 mb-4 md:mb-6 flex-wrap">
            <button onClick={() => navigate('/')} className="hover:text-blue-600">خانه</button>
            <ChevronRight className="w-3 h-3 md:w-4 md:h-4" />
            <button onClick={() => navigate('/products')} className="hover:text-blue-600">محصولات</button>
            <ChevronRight className="w-3 h-3 md:w-4 md:h-4" />
            <span className="text-gray-900 font-medium line-clamp-1 max-w-[150px]">{category.name}</span>
          </div>

          {/* هدر دسته‌بندی */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 md:p-6 mb-6 md:mb-8 shadow-sm">
            <div className="flex items-center gap-3 md:gap-4 flex-wrap">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-white rounded-full flex items-center justify-center shadow-md">
                {category.icon ? (
                  <img 
                    src={`${process.env.REACT_APP_API_URL || ''}${category.icon}`} 
                    alt={category.name}
                    className="w-7 h-7 md:w-10 md:h-10 object-contain"
                  />
                ) : (
                  getCategoryIcon(category.name)
                )}
              </div>
              <div>
                <h1 className="text-xl md:text-3xl font-bold text-gray-900">{category.name}</h1>
                {category.description && (
                  <p className="text-gray-600 text-sm md:text-base mt-1 max-w-2xl line-clamp-2">{category.description}</p>
                )}
                <div className="flex items-center gap-4 mt-2">
                  <span className="text-xs md:text-sm text-gray-500">{totalCount} محصول</span>
                </div>
              </div>
            </div>
          </div>

          {/* زیردسته‌ها */}
          {subcategories.length > 0 && (
            <div className="mb-6 md:mb-8">
              <h2 className="text-base md:text-lg font-bold text-gray-900 mb-3 md:mb-4">زیردسته‌ها</h2>
              <div className="flex flex-wrap gap-2 md:gap-3">
                {subcategories.map((sub) => (
                  <Link
                    key={sub.id}
                    to={`/category/${sub.slug}`}
                    className="px-3 py-1.5 md:px-4 md:py-2 bg-white rounded-lg border border-gray-200 hover:border-blue-500 hover:text-blue-600 transition text-xs md:text-sm"
                  >
                    {sub.name}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* نوار ابزار - واکنش‌گرا */}
          <div className="flex flex-wrap justify-between items-center gap-3 md:gap-4 mb-4 md:mb-6">
            <div className="flex items-center gap-2">
              {/* دکمه فیلتر موبایل */}
              <button
                onClick={() => setShowMobileFilters(true)}
                className="lg:hidden flex items-center gap-2 bg-gray-100 text-gray-700 px-3 py-2 rounded-lg text-sm"
              >
                <SlidersHorizontal className="w-4 h-4" />
                فیلتر
                {hasActiveFilters && (
                  <span className="bg-blue-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                    !
                  </span>
                )}
              </button>

              <div className="flex items-center gap-1 bg-white rounded-lg border p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 md:p-2 rounded transition ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-500'}`}
                  title="نمایش ستونی"
                >
                  <Grid3x3 className="w-3.5 h-3.5 md:w-5 md:h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 md:p-2 rounded transition ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-500'}`}
                  title="نمایش لیستی"
                >
                  <List className="w-3.5 h-3.5 md:w-5 md:h-5" />
                </button>
              </div>
            </div>

            {/* تعداد در هر صفحه - مخفی در موبایل */}
            <div className="hidden md:flex items-center gap-2">
              <span className="text-sm text-gray-500">تعداد در صفحه:</span>
              <div className="flex items-center gap-1 bg-white rounded-lg border p-1">
                {PAGE_SIZE_OPTIONS.map(size => (
                  <button
                    key={size}
                    onClick={() => handlePageSizeChange(size)}
                    className={`px-2 py-1 rounded transition text-xs ${pageSize === size ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            {/* مرتب‌سازی */}
            <div className="flex items-center gap-2">
              <span className="text-xs md:text-sm text-gray-500">مرتب‌سازی:</span>
              <select
                value={filters.sort}
                onChange={(e) => handleFilterChange('sort', e.target.value)}
                className="px-2 py-1.5 md:px-3 md:py-2 bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm"
              >
                <option value="-created_at">جدیدترین</option>
                <option value="price">ارزان‌ترین</option>
                <option value="-price">گران‌ترین</option>
                <option value="name">نام محصول</option>
                <option value="-view_count">پربازدیدترین</option>
              </select>
            </div>
          </div>

          {/* محتوای اصلی */}
          <div className="flex flex-col lg:flex-row gap-4 md:gap-8">
            {/* فیلترها - سایدبار دسکتاپ */}
            <div className="hidden lg:block lg:w-64">
              <div className="bg-white rounded-xl p-4 sticky top-24">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-bold text-gray-900">فیلترها</h3>
                  {hasActiveFilters && (
                    <button onClick={clearFilters} className="text-sm text-red-500 hover:text-red-600">
                      حذف همه
                    </button>
                  )}
                </div>

                <div className="mb-6">
                  <h4 className="font-medium text-gray-800 mb-3">محدوده قیمت (تومان)</h4>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      placeholder="حداقل"
                      value={filters.minPrice}
                      onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                      className="w-1/2 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                    <input
                      type="number"
                      placeholder="حداکثر"
                      value={filters.maxPrice}
                      onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                      className="w-1/2 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="font-medium text-gray-800 mb-3">وضعیت موجودی</h4>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.inStock}
                      onChange={(e) => handleFilterChange('inStock', e.target.checked)}
                      className="rounded w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">فقط محصولات موجود در انبار</span>
                  </label>
                </div>

                {hasActiveFilters && (
                  <div className="mt-6 pt-4 border-t">
                    <h4 className="font-medium text-gray-800 mb-3">فیلترهای فعال:</h4>
                    <div className="flex flex-wrap gap-2">
                      {filters.minPrice && (
                        <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded">
                          حداقل {parseInt(filters.minPrice).toLocaleString()} تومان
                          <button onClick={() => handleFilterChange('minPrice', '')} className="hover:text-red-500">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      )}
                      {filters.maxPrice && (
                        <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded">
                          حداکثر {parseInt(filters.maxPrice).toLocaleString()} تومان
                          <button onClick={() => handleFilterChange('maxPrice', '')} className="hover:text-red-500">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      )}
                      {filters.inStock && (
                        <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs px-2 py-1 rounded">
                          فقط موجود
                          <button onClick={() => handleFilterChange('inStock', false)} className="hover:text-red-500">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* محصولات */}
            <div className="flex-1">
              {products.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Package className="w-10 h-10 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">محصولی یافت نشد</h3>
                  <p className="text-gray-500 mb-4 text-sm">هیچ محصولی با این فیلترها وجود ندارد</p>
                  {hasActiveFilters && (
                    <button onClick={clearFilters} className="text-blue-600 hover:underline text-sm">
                      حذف فیلترها
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="text-xs md:text-sm text-gray-500 mb-3 md:mb-4 flex justify-between items-center">
                    <span>نمایش {startItem} تا {endItem} از {totalCount} محصول</span>
                  </div>
                  
                  <div className={
                    viewMode === 'grid'
                      ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6'
                      : 'space-y-3 md:space-y-4'
                  }>
                    {products.map((product) => (
                      <ProductCard key={product.id} product={product} viewMode={viewMode} />
                    ))}
                  </div>

                  {/* صفحه‌بندی کامل - واکنش‌گرا */}
                  {totalPages > 1 && (
                    <div className="mt-6 md:mt-8 flex justify-center items-center gap-1 md:gap-2 flex-wrap">
                      <button
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                        className="p-1.5 md:p-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                        title="اولین صفحه"
                      >
                        <ChevronsRight className="w-4 h-4 md:w-5 md:h-5" />
                      </button>
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="p-1.5 md:p-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                        title="قبلی"
                      >
                        <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
                      </button>
                      
                      <div className="flex gap-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`w-8 h-8 md:w-10 md:h-10 rounded-lg transition text-sm md:text-base ${
                                currentPage === pageNum
                                  ? 'bg-blue-600 text-white'
                                  : 'border hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="p-1.5 md:p-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                        title="بعدی"
                      >
                        <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
                      </button>
                      <button
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                        className="p-1.5 md:p-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                        title="آخرین صفحه"
                      >
                        <ChevronsLeft className="w-4 h-4 md:w-5 md:h-5" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* فیلتر موبایل - Drawer از پایین */}
      {showMobileFilters && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setShowMobileFilters(false)}
          />
          <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-xl z-50 max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900">فیلترها</h3>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <div className="mb-6">
                <h4 className="font-medium text-gray-800 mb-3">محدوده قیمت (تومان)</h4>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="حداقل"
                    value={filters.minPrice}
                    onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                    className="w-1/2 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                  <input
                    type="number"
                    placeholder="حداکثر"
                    value={filters.maxPrice}
                    onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                    className="w-1/2 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-medium text-gray-800 mb-3">وضعیت موجودی</h4>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.inStock}
                    onChange={(e) => handleFilterChange('inStock', e.target.checked)}
                    className="rounded w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">فقط محصولات موجود در انبار</span>
                </label>
              </div>

              {hasActiveFilters && (
                <div className="mb-6 pt-4 border-t">
                  <h4 className="font-medium text-gray-800 mb-3">فیلترهای فعال:</h4>
                  <div className="flex flex-wrap gap-2">
                    {filters.minPrice && (
                      <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded">
                        حداقل {parseInt(filters.minPrice).toLocaleString()} تومان
                        <button onClick={() => handleFilterChange('minPrice', '')} className="hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    )}
                    {filters.maxPrice && (
                      <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded">
                        حداکثر {parseInt(filters.maxPrice).toLocaleString()} تومان
                        <button onClick={() => handleFilterChange('maxPrice', '')} className="hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    )}
                    {filters.inStock && (
                      <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs px-2 py-1 rounded">
                        فقط موجود
                        <button onClick={() => handleFilterChange('inStock', false)} className="hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="sticky bottom-0 bg-white p-4 border-t">
              <button
                onClick={() => setShowMobileFilters(false)}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium"
              >
                اعمال فیلترها
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};